// Service Status Dashboard — health monitoring + incident management

import { AlertTriangle, Plus, Radio } from 'lucide-react'
import { useState } from 'react'
import { IncidentForm } from '@/components/status/incident-form'
import { IncidentList } from '@/components/status/incident-list'
import { RecentIncidentsTable } from '@/components/status/recent-incidents-table'
import { ServiceCard } from '@/components/status/service-card'
import { LoadingPage } from '@/components/ui/loading'
import { MetricCard } from '@/components/ui/metric-card'
import { Modal } from '@/components/ui/modal'
import {
  useActiveIncidents,
  useAddIncidentUpdate,
  useCreateIncident,
  useDeleteIncident,
  useRecentIncidents,
  useSystemStatus,
  useUpdateIncident,
} from '@/hooks/use-status'

function overallVariant(overall: string): 'default' | 'success' | 'danger' | 'warning' {
  if (overall === 'operational') return 'success'
  if (overall === 'major_outage') return 'danger'
  if (overall === 'degraded') return 'warning'
  return 'default'
}

function overallLabel(overall: string): string {
  if (overall === 'operational') return 'All Systems Operational'
  if (overall === 'degraded') return 'Degraded Performance'
  if (overall === 'major_outage') return 'Major Outage'
  if (overall === 'maintenance') return 'Under Maintenance'
  return overall.replace(/_/g, ' ')
}

export function StatusPage(): React.JSX.Element {
  const statusQuery = useSystemStatus()
  const activeQuery = useActiveIncidents()
  const recentQuery = useRecentIncidents()
  const createMutation = useCreateIncident()
  const addUpdateMutation = useAddIncidentUpdate()
  const updateMutation = useUpdateIncident()
  const deleteMutation = useDeleteIncident()

  const [showCreateModal, setShowCreateModal] = useState(false)

  const services = statusQuery.data?.services ?? []
  const overall = statusQuery.data?.overall ?? 'unknown'
  const activeIncidents = activeQuery.data?.incidents ?? []
  const recentIncidents = recentQuery.data?.incidents ?? []

  const operationalCount = services.filter((s) => s.status === 'operational').length
  const avgResponseTime =
    services.length > 0
      ? Math.round(services.reduce((sum, s) => sum + s.responseTimeMs, 0) / services.length)
      : 0

  function handleCreateIncident(data: {
    title: string
    severity: string
    affectedServices: string[]
    message: string
  }): void {
    createMutation.mutate(data, {
      onSuccess: () => setShowCreateModal(false),
    })
  }

  function handleAddUpdate(id: number, data: { status: string; message: string }): void {
    addUpdateMutation.mutate({ id, data })
  }

  function handleResolve(id: number): void {
    updateMutation.mutate({ id, data: { status: 'resolved' } })
  }

  function handleDelete(id: number): void {
    deleteMutation.mutate(id)
  }

  if (statusQuery.isLoading) {
    return <LoadingPage message="Loading service status..." />
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Radio className="h-6 w-6 text-admin-500" />
          <h1 className="text-2xl font-semibold text-text-primary">Service Status</h1>
        </div>
        <button
          type="button"
          onClick={() => setShowCreateModal(true)}
          className="flex items-center gap-2 rounded-lg bg-admin-500 px-4 py-2 text-sm font-medium text-white hover:bg-admin-600"
        >
          <Plus className="h-4 w-4" />
          Create Incident
        </button>
      </div>

      {/* Summary cards */}
      <div className="grid gap-4 md:grid-cols-4">
        <MetricCard
          label="Overall Status"
          value={overallLabel(overall)}
          variant={overallVariant(overall)}
        />
        <MetricCard
          label="Services Up"
          value={`${operationalCount}/${services.length}`}
          variant={operationalCount === services.length ? 'success' : 'warning'}
        />
        <MetricCard label="Avg Response Time" value={`${avgResponseTime}ms`} />
        <MetricCard
          label="Active Incidents"
          value={String(activeIncidents.length)}
          variant={activeIncidents.length > 0 ? 'danger' : 'success'}
        />
      </div>

      {/* Service health cards */}
      <div>
        <h2 className="mb-3 text-lg font-semibold text-text-primary">Services</h2>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {services.map((service) => (
            <ServiceCard key={service.serviceId} service={service} />
          ))}
        </div>
      </div>

      {/* Active incidents */}
      <div>
        <div className="mb-3 flex items-center gap-2">
          <AlertTriangle className="h-5 w-5 text-yellow-500" />
          <h2 className="text-lg font-semibold text-text-primary">Active Incidents</h2>
        </div>
        <IncidentList
          incidents={activeIncidents}
          onAddUpdate={handleAddUpdate}
          onResolve={handleResolve}
          isUpdating={addUpdateMutation.isPending || updateMutation.isPending}
        />
      </div>

      {/* Recent incidents */}
      <div>
        <h2 className="mb-3 text-lg font-semibold text-text-primary">Recent Incidents</h2>
        <RecentIncidentsTable
          incidents={recentIncidents}
          onDelete={handleDelete}
          isDeleting={deleteMutation.isPending}
        />
      </div>

      {/* Create incident modal */}
      <Modal
        open={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        title="Create Incident"
        size="lg"
      >
        <IncidentForm
          services={services}
          onSubmit={handleCreateIncident}
          isPending={createMutation.isPending}
          onCancel={() => setShowCreateModal(false)}
        />
      </Modal>
    </div>
  )
}
